/**
 * Access-request review dialog (finalization WS4) — opened from an
 * ACCESS_REQUEST_PENDING bell item. Shows the request (who, what, why), a
 * role picker over the tenant's ACTIVE roles (instances — the same vocabulary
 * as the grant dialog), and approve/deny with a reason. The server enforces
 * the reviewer gate; this dialog is just the affordance.
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Dialog, DialogContent, DialogHeader, DialogTitle, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraButton, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { useAccessRequest, useDecideAccessRequest, useGrantableRoles } from '../../pages/Admin/Access/hooks';
import { ResolvedId } from '../../pages/Admin/nameResolution';
// A request's window (delegation only) — matches AssignmentsListPage's
// formatTimestamp for the same {seconds, nanos} shape.
function formatTimestamp(ts) {
    if (!ts || Number(ts.seconds) === 0) return '—';
    return new Date(Number(ts.seconds) * 1000).toLocaleDateString();
}
export function ReviewAccessRequestDialog({ requestId, open, onOpenChange }) {
    const [roleKey, setRoleKey] = useState('');
    const [reason, setReason] = useState('');
    // A dialog reopened for a DIFFERENT request must not carry the previous
    // pick — "Approve & grant" was enabled with a role chosen for someone
    // else (round 10).
    useEffect(()=>{
        setRoleKey('');
        setReason('');
    }, [
        requestId,
        open
    ]);
    const { data: request } = useAccessRequest(open ? requestId : '');
    // Member-grade role vocabulary (round 7): a non-admin grantor could
    // Decide on the backend but the picker loaded through an admin-only RPC.
    const { data: bundles } = useGrantableRoles(open);
    const decide = useDecideAccessRequest();
    const isDelegation = request?.intent === 'delegation';
    const pending = request?.status === 'pending';
    const submit = async (approve)=>{
        try {
            await decide.mutateAsync({
                id: requestId,
                approve,
                roleBundleKey: approve ? roleKey : '',
                reason: reason.trim()
            });
        } catch  {
            return; // hook toasts; keep the dialog open
        }
        onOpenChange(false);
    };
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            "data-testid": Testids.notification.reviewAccessDialog,
            children: [
                /*#__PURE__*/ _jsx(DialogHeader, {
                    children: /*#__PURE__*/ _jsx(DialogTitle, {
                        children: "Review access request"
                    })
                }),
                !request ? /*#__PURE__*/ _jsx("p", {
                    className: "text-sm text-muted-foreground",
                    children: "Loading request…"
                }) : /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "rounded-md border bg-muted/20 px-3 py-2 text-sm",
                            children: [
                                /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        /*#__PURE__*/ _jsx(ResolvedId, {
                                            id: request.requesterUserId,
                                            name: request.requesterDisplayName
                                        }),
                                        ' ',
                                        "asked to ",
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "font-medium",
                                            children: request.intent
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-xs text-muted-foreground",
                                    "data-testid": Testids.notification.reviewAccessTarget,
                                    children: [
                                        request.targetObjectType,
                                        " ·",
                                        ' ',
                                        /*#__PURE__*/ _jsx(ResolvedId, {
                                            id: request.targetObjectId,
                                            name: request.targetDisplayName
                                        })
                                    ]
                                }),
                                request.message && /*#__PURE__*/ _jsxs("p", {
                                    className: "mt-1 text-xs italic",
                                    children: [
                                        "“",
                                        request.message,
                                        "”"
                                    ]
                                }),
                                isDelegation && /*#__PURE__*/ _jsxs("div", {
                                    className: "mt-2 space-y-0.5 border-t pt-2",
                                    children: [
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "text-xs",
                                            "data-testid": Testids.notification.reviewAccessDelegationOwner,
                                            children: [
                                                "Owner:",
                                                ' ',
                                                /*#__PURE__*/ _jsx(ResolvedId, {
                                                    id: request.originalOwnerUserId,
                                                    name: request.originalOwnerDisplayName
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "text-xs",
                                            "data-testid": Testids.notification.reviewAccessDelegationDelegate,
                                            children: [
                                                "Delegate:",
                                                ' ',
                                                /*#__PURE__*/ _jsx(ResolvedId, {
                                                    id: request.delegateUserId,
                                                    name: request.delegateDisplayName
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "text-xs text-muted-foreground",
                                            "data-testid": Testids.notification.reviewAccessDelegationWindow,
                                            children: [
                                                "Window: ",
                                                formatTimestamp(request.effectiveStart),
                                                " –",
                                                ' ',
                                                formatTimestamp(request.effectiveEnd)
                                            ]
                                        })
                                    ]
                                }),
                                !pending && /*#__PURE__*/ _jsxs("p", {
                                    className: "mt-1 text-xs font-medium text-amber-700",
                                    children: [
                                        "Already ",
                                        request.status,
                                        request.decisionReason ? ` — ${request.decisionReason}` : '',
                                        "."
                                    ]
                                })
                            ]
                        }),
                        pending && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                !isDelegation && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx(Label, {
                                            children: "Role to grant on approval"
                                        }),
                                        /*#__PURE__*/ _jsxs(Select, {
                                            value: roleKey,
                                            onValueChange: setRoleKey,
                                            children: [
                                                /*#__PURE__*/ _jsx(SelectTrigger, {
                                                    "data-testid": Testids.notification.reviewAccessRole,
                                                    children: /*#__PURE__*/ _jsx(SelectValue, {
                                                        placeholder: "Choose an access level"
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx(SelectContent, {
                                                    children: (bundles ?? []).map((b)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                            value: b.key,
                                                            children: b.displayName
                                                        }, b.key))
                                                })
                                            ]
                                        }),
                                        roleKey && /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs text-muted-foreground",
                                            children: bundles?.find((b)=>b.key === roleKey)?.description ?? ''
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx(Label, {
                                            children: "Reason"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraTextarea, {
                                            value: reason,
                                            placeholder: "Recorded in the grant history either way",
                                            onChange: (e)=>setReason(e.target.value)
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                pending && /*#__PURE__*/ _jsxs("div", {
                    className: "flex justify-end gap-2",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "destructive",
                            "data-testid": Testids.notification.reviewAccessDeny,
                            disabled: decide.isPending,
                            onClick: ()=>submit(false),
                            children: "Decline"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": Testids.notification.reviewAccessApprove,
                            disabled: !isDelegation && !roleKey || decide.isPending,
                            onClick: ()=>submit(true),
                            children: "Approve & grant"
                        })
                    ]
                })
            ]
        })
    });
}
